import { useState, useEffect, useRef } from 'react';
import styles from './ProdutoInformacao.module.css';
import TagsDisplay from './TagsDisplay';

/**
 * Componente para selecionar informação do produto
 * Agora suporta MÚLTIPLAS TAGS simultaneamente
 * @param {Object} props
 * @param {string} props.produtoCodigo - Código do produto
 * @param {string|Array} props.valorAtual - Valor atual (null, string ou array de tags)
 * @param {string} props.tipo - 'ecommerce' ou 'loja-fisica'
 * @param {Function} props.onUpdate - Callback após atualização
 */
const ProdutoInformacao = ({ produtoCodigo, valorAtual, tipo, onUpdate }) => {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  // Converter valorAtual para array de tags
  const parseValorAtual = (valor) => {
    if (!valor) return [];
    if (Array.isArray(valor)) return valor;
    if (typeof valor === 'string') {
      try {
        const parsed = JSON.parse(valor);
        return Array.isArray(parsed) ? parsed : [valor];
      } catch {
        return [valor];
      }
    }
    return [];
  };

  const [tags, setTags] = useState(parseValorAtual(valorAtual));
  const [isLoading, setIsLoading] = useState(false);

  // Atualizar tags quando valorAtual mudar
  useEffect(() => {
    setTags(parseValorAtual(valorAtual));
  }, [valorAtual]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Toggle de uma tag específica
  const toggleTag = async (tag) => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      // Adicionar ou remover tag
      const novasTags = tags.includes(tag)
        ? tags.filter(t => t !== tag)
        : [...tags, tag];

      const endpoint = tipo === 'ecommerce'
        ? `/api/produtos/ecommerce/${produtoCodigo}/informacao`
        : `/api/produtos/loja-fisica/${produtoCodigo}/informacao`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          informacao: novasTags.length > 0 ? novasTags : null
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar informação');
      }

      setTags(novasTags);

      if (onUpdate) {
        onUpdate(novasTags);
      }

    } catch (error) {
      console.error('Erro ao atualizar informação:', error);
      alert('Erro ao atualizar informação do produto');
    } finally {
      setIsLoading(false);
    }
  };

  // Limpar todas as tags
  const limparTags = async () => {
    if (isLoading || tags.length === 0) return;

    setIsLoading(true);

    try {
      const endpoint = tipo === 'ecommerce'
        ? `/api/produtos/ecommerce/${produtoCodigo}/informacao`
        : `/api/produtos/loja-fisica/${produtoCodigo}/informacao`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ informacao: null })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar informação');
      }

      setTags([]);

      if (onUpdate) {
        onUpdate(null);
      }

    } catch (error) {
      console.error('Erro ao atualizar informação:', error);
      alert('Erro ao atualizar informação do produto');
    } finally {
      setIsLoading(false);
    }
  };

  // Verificar se uma tag está ativa
  const isTagAtiva = (tag) => tags.includes(tag);

  // Obter label do botão principal
  const getButtonLabel = () => {
    if (isLoading) return 'Salvando...';
    if (tags.length === 0) return 'Normal';
    if (tags.length === 1) {
      if (tags[0] === 'recorrente') return 'Recorrente';
      if (tags[0] === 'nao_comprar') return 'Não Comprar';
      if (tags[0] === 'mudar_foto') return 'Mudar Foto';
    }
    return `${tags.length} Tags`;
  };

  // Obter classe do botão principal
  const getButtonClassName = () => {
    if (tags.length === 0) return styles.tagNormal;
    if (tags.length === 1) {
      if (tags[0] === 'recorrente') return styles.tagRecorrente;
      if (tags[0] === 'nao_comprar') return styles.tagNaoComprar;
      if (tags[0] === 'mudar_foto') return styles.tagMudarFoto;
    }
    return styles.tagMultiplas;
  };

  return (
    <div className={styles.container} ref={dropdownRef}>
      {/* Exibição das tags (clicável para editar) */}
      <div
        className={styles.tagsDisplayWrapper}
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        title="Clique para editar"
      >
        <TagsDisplay tags={tags} />
      </div>

      {/* Dropdown de edição */}
      {isOpen && (
        <div className={styles.dropdownContent}>
          <div className={styles.dropdownHeader}>
            <span>Selecione as tags (múltiplas)</span>
            {tags.length > 0 && (
              <button
                className={styles.limparButton}
                onClick={limparTags}
                disabled={isLoading}
              >
                Limpar
              </button>
            )}
          </div>

          <div className={styles.divider}></div>

          <button
            className={`${styles.dropdownItem} ${isTagAtiva('recorrente') ? styles.active : ''}`}
            onClick={() => toggleTag('recorrente')}
            disabled={isLoading}
          >
            <span className={styles.checkbox}>
              {isTagAtiva('recorrente') ? '☑' : '☐'}
            </span>
            <span className={styles.iconRecorrente}>🔄</span>
            <span>Compra Recorrente</span>
          </button>

          <button
            className={`${styles.dropdownItem} ${isTagAtiva('nao_comprar') ? styles.active : ''}`}
            onClick={() => toggleTag('nao_comprar')}
            disabled={isLoading}
          >
            <span className={styles.checkbox}>
              {isTagAtiva('nao_comprar') ? '☑' : '☐'}
            </span>
            <span className={styles.iconNaoComprar}>⊘</span>
            <span>Não Comprar Mais</span>
          </button>

          {isLoading && (
            <div className={styles.loadingOverlay}>
              <span>Salvando...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProdutoInformacao;

